import { useEffect, useState } from 'react'
import { Mail } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../lib/shadcn/dialog'
import { Button } from '../../lib/shadcn/button'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { useGetOwnerStatementEmailPreview, useSendOwnerStatementEmail } from '../../hooks/backend/statements'

export function SendStatementDialog({
  statementId,
  disabled,
  disabledReason,
}: {
  statementId: string
  // Disabled up front from the caller (e.g. missing owner_id) so the button
  // never even opens a dialog that cannot send.
  disabled: boolean
  disabledReason: string | null
}) {
  const [open, setOpen] = useState(false)
  const preview = useGetOwnerStatementEmailPreview()
  const send = useSendOwnerStatementEmail()
  const previewTrigger = preview.trigger

  useEffect(() => {
    if (open) previewTrigger({ statement_id: statementId }, { skipCache: true })
  }, [open, statementId, previewTrigger])

  const data = preview.data as
    | { to: string | null; subject: string; html: string; disabledReason: string | null }
    | undefined

  const handleSend = async () => {
    try {
      const result = (await send.trigger({ statement_id: statementId }).result) as
        | { ok: boolean; to: string }
        | undefined
      toast.success('Statement sent', {
        description: result?.to ? `Sent to ${result.to}.` : undefined,
      })
      setOpen(false)
    } catch (err) {
      toast.error('Could not send statement', {
        description: err instanceof Error ? err.message : String(err),
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled}
        title={disabled ? disabledReason ?? undefined : undefined}
        onClick={() => setOpen(true)}
        className="gap-1.5"
      >
        <Mail className="h-3.5 w-3.5" />
        Send statement
      </Button>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Send statement</DialogTitle>
          <DialogDescription>
            Review the email before it goes out. This sends an HTML email to the owner; it does not
            attach a PDF, since no statement in the system has one on file yet.
          </DialogDescription>
        </DialogHeader>

        {preview.loading ? (
          <div className="flex items-center justify-center gap-2 p-10 text-sm text-muted-foreground">
            <Spinner className="h-4 w-4" /> Building preview...
          </div>
        ) : preview.error ? (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            {preview.error}
          </div>
        ) : data ? (
          <div className="space-y-3">
            <div className="rounded-md border border-border bg-muted/40 p-3 text-sm">
              <p>
                <span className="font-medium text-foreground">To: </span>
                <span className="text-muted-foreground">{data.to ?? 'No recipient'}</span>
              </p>
              <p className="mt-1">
                <span className="font-medium text-foreground">Subject: </span>
                <span className="text-muted-foreground">{data.subject}</span>
              </p>
            </div>
            {data.disabledReason && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
                {data.disabledReason}
              </div>
            )}
            <div className="rounded-lg border border-border bg-white p-4 dark:bg-white">
              <iframe
                title="Statement email preview"
                srcDoc={data.html}
                className="h-[420px] w-full border-0"
              />
            </div>
          </div>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSend}
            disabled={send.loading || !data || !data.to || !!data.disabledReason}
          >
            {send.loading ? <Spinner className="h-4 w-4" /> : 'Send'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
